import { Checkbox } from '@/global/components/Checkbox'
import { Badge } from '@/global/components/ui/badge'
import { Button } from '@/global/components/ui/button'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/global/components/ui/command'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/global/components/ui/popover'
import { cn } from '@/lib/utils'
import { ChevronsUpDownIcon, Loader2Icon } from 'lucide-react'
import React, { useContext, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import CircularProgress from '../CircularProgress'
import { Label } from '../ui/label'
import { ShellContext, useEscopoContext } from './context'
import { arr, type FacetaId, guiasDaArvore, LABEL } from './escopo'
import type { Opcao } from './useFacetaOptions'

type Props = {
    id: FacetaId
    multiple?: boolean
    /** lista as opções de todas as empresas do cliente, ignorando o escopo */
    listAll?: boolean
}

/**
 * Parte genérica. Registra a própria multiplicidade e o `listAll` no Root no
 * mount — é esse registro que dá ordem ao rail, diz como zerar a faceta no
 * reset e de onde vêm as opções.
 */
export function Faceta({ id, multiple = false, listAll = false }: Props) {
    const { registrar, ativa, painel } = useEscopoContext()
    const shell = useContext(ShellContext)

    useEffect(
        () => registrar({ id, multiple, listAll }),
        [id, multiple, listAll, registrar],
    )

    const lista = <ListaFaceta id={id} multiple={multiple} />

    if (shell === 'inline') return <FacetaInline id={id}>{lista}</FacetaInline>
    if (!painel || ativa !== id) return null
    return createPortal(lista, painel)
}

function ContainerSemResultado({
    children,
    className,
}: {
    children: React.ReactNode
    className?: string
}) {
    return (
        <CommandEmpty
            className={cn(
                'inline-flex items-center gap-2',
                'px-20 py-12 text-center text-muted-foreground',
                'font-medium select-none',
                className,
            )}
        >
            {children}
        </CommandEmpty>
    )
}

/**
 * Lista da faceta. A de funcionário busca e pagina no servidor (#1870), então
 * o `cmdk` não filtra nada nela — filtrar o que já veio esconderia resultado
 * que o servidor mandou. As outras seguem com filtro local.
 */
function ListaFaceta({ id, multiple }: { id: FacetaId; multiple: boolean }) {
    const { valor, setFaceta, dados, busca, setBusca } = useEscopoContext()
    const faceta = dados[id]
    const selecionados = arr(valor[id])
    const vazio = !faceta.carregando && !faceta.opcoes.length
    const isServidor = id === 'funcionarios'

    const [ativos, demitidos] = [
        faceta.opcoes.filter(opcao => opcao.situacao !== 'demitido'),
        faceta.opcoes.filter(opcao => opcao.situacao === 'demitido'),
    ]

    const guiasAtivos = guiasDaArvore(ativos.map(opcao => opcao.indent ?? 0))
    const guiasDemitidos = guiasDaArvore(
        demitidos.map(opcao => opcao.indent ?? 0),
    )

    const alternar = (opcaoId: number) => {
        if (!multiple) {
            setFaceta(id, selecionados[0] === opcaoId ? null : opcaoId)
            return
        }
        setFaceta(
            id,
            selecionados.includes(opcaoId)
                ? selecionados.filter(atual => atual !== opcaoId)
                : [...selecionados, opcaoId],
        )
    }

    return (
        <div
            className="flex size-full flex-col"
            onClick={e => e.stopPropagation()}
            onPointerDown={e => e.stopPropagation()}
            onKeyDown={e => {
                if (e.key !== 'Escape') e.stopPropagation()
            }}
        >
            <Command
                shouldFilter={!isServidor}
                className="h-full bg-transparent [&_[data-slot=command-input-wrapper]]:h-12"
                label={LABEL[id]}
            >
                <CommandInput
                    placeholder={`Buscar em ${LABEL[id].toLowerCase()}…`}
                    {...(isServidor && {
                        value: busca,
                        onValueChange: setBusca,
                    })}
                />
                <CommandList className="min-h-0 max-h-[300px] flex-1 overflow-y-auto bg-transparent p-1">
                    {(() => {
                        if (faceta.carregando && !faceta.opcoes.length) {
                            return (
                                <ContainerSemResultado>
                                    <CircularProgress size={24} />
                                    Carregando {LABEL[id].toLowerCase()}…
                                </ContainerSemResultado>
                            )
                        } else if (vazio && !faceta.carregando) {
                            return (
                                <ContainerSemResultado>
                                    {faceta.vazioTexto}
                                </ContainerSemResultado>
                            )
                        } else {
                            return (
                                <ContainerSemResultado>
                                    Nenhum resultado encontrado.
                                </ContainerSemResultado>
                            )
                        }
                    })()}
                    <CommandGroup className="p-0 space-y-0.5">
                        {ativos.map((opcao, indice) => (
                            <ItemFaceta
                                key={opcao.id}
                                opcao={opcao}
                                marcada={selecionados.includes(opcao.id)}
                                onSelecionar={() => alternar(opcao.id)}
                                guias={guiasAtivos[indice]}
                            />
                        ))}
                    </CommandGroup>
                    {!!demitidos.length && (
                        <CommandGroup
                            heading="Demitidos"
                            className="p-0 space-y-0.5"
                        >
                            {demitidos.map((opcao, indice) => (
                                <ItemFaceta
                                    key={opcao.id}
                                    opcao={opcao}
                                    marcada={selecionados.includes(opcao.id)}
                                    onSelecionar={() => alternar(opcao.id)}
                                    guias={guiasDemitidos[indice]}
                                />
                            ))}
                        </CommandGroup>
                    )}
                    {faceta.temMais && (
                        <SentinelaProximaPagina
                            onVisivel={faceta.carregarMais}
                            carregando={faceta.carregandoMais}
                        />
                    )}
                </CommandList>
            </Command>
        </div>
    )
}

/**
 * Coluna de guia de um nível da árvore, com a largura do checkbox — é o que
 * produz a indentação, no lugar de um `padding-left` calculado.
 *
 * `continua`: a linha desse nível segue para o item de baixo. `cotovelo`: é o
 * nível do próprio item, então a linha termina aqui com a curva.
 */
function GuiaNivel({
    continua,
    cotovelo,
}: {
    continua: boolean
    cotovelo: boolean
}) {
    // `-ml-px` centraliza a borda de 2px na coluna
    const traco = 'absolute left-1/2 -ml-px border-l-2 border-border'
    // `-top-4` entra no item de cima, para as guias se emendarem linha a linha
    const doTopo = cn(traco, '-top-4')

    // nível já encerrado acima e que não é o do item: coluna só indenta
    if (!continua && !cotovelo) return <span className="w-5 shrink-0" />

    if (!cotovelo)
        return (
            <span className="relative w-5 shrink-0">
                <span className={cn(doTopo, 'bottom-0')} />
            </span>
        )

    return (
        <span className="relative w-5 shrink-0">
            {/* para no centro da linha e curva para o checkbox, como no YouTube */}
            <span
                className={cn(
                    doTopo,
                    'right-0 h-[calc(50%+1rem)] rounded-bl-lg border-b-2',
                )}
            />
            {/* não é o último filho: o nível segue abaixo da curva */}
            {continua && <span className={cn(traco, 'top-1/2 bottom-0')} />}
        </span>
    )
}

function ItemFaceta({
    opcao,
    marcada,
    onSelecionar,
    guias,
}: {
    opcao: Opcao
    marcada: boolean
    onSelecionar: () => void
    /** um por nível acima do item: se a linha daquele nível continua abaixo */
    guias: boolean[]
}) {
    return (
        <CommandItem
            value={`${opcao.descricao ?? ''} ${opcao.label}`}
            onSelect={onSelecionar}
            data-marcada={marcada}
            className={cn(
                'flex items-center gap-2.5 rounded-lg px-2.5 py-2 cursor-pointer',
                'data-[selected=true]:bg-accent text-foreground!',
                'hover:bg-accent transition-colors select-none my-0.5',
                marcada && 'bg-accent',
            )}
        >
            {!!guias.length && (
                // `-mr-2.5` anula o `gap-2.5` da linha, para o braço do
                // cotovelo encostar no checkbox; `-my-0.5` cobre o vão entre
                // as linhas, para as guias se emendarem de item a item.
                <span
                    aria-hidden
                    className="-my-0.5 -mr-2.5 flex shrink-0 self-stretch"
                >
                    {guias.map((continua, nivel) => (
                        <GuiaNivel
                            key={`${opcao.id}-${nivel}`}
                            continua={continua}
                            cotovelo={nivel === guias.length - 1}
                        />
                    ))}
                </span>
            )}
            {/* decorativo: quem alterna é o `onSelect` do `CommandItem` */}
            <Checkbox
                checked={marcada}
                aria-hidden
                tabIndex={-1}
                className="pointer-events-none shrink-0"
            />
            <span className="truncate flex-1">{opcao.label}</span>
            {opcao.descricao && (
                <Label
                    title={opcao.titleDescricao}
                    className={cn(
                        'shrink-0 text-xs tabular-nums',
                        'text-muted-foreground px-1.5 py-0.5 rounded border',
                        {
                            'cursor-help': !!opcao.titleDescricao,
                        },
                    )}
                >
                    {opcao.descricao}
                </Label>
            )}
        </CommandItem>
    )
}

/** Pede a próxima página quando entra em tela — rolagem infinita da lista. */
function SentinelaProximaPagina({
    onVisivel,
    carregando,
}: {
    onVisivel?: () => void
    carregando?: boolean
}) {
    const alvo = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const elemento = alvo.current
        if (!elemento || !onVisivel) return

        const observador = new IntersectionObserver(([entrada]) => {
            if (entrada.isIntersecting) onVisivel()
        })

        observador.observe(elemento)
        return () => observador.disconnect()
    }, [onVisivel])

    return (
        <div
            ref={alvo}
            className="flex items-center justify-center py-3 text-xs text-muted-foreground select-none"
        >
            {carregando && (
                <Loader2Icon className="size-4 animate-spin text-primary" />
            )}
        </div>
    )
}

/** No shell inline cada faceta é um botão com popover próprio. */
function FacetaInline({
    id,
    children,
}: {
    id: FacetaId
    children: React.ReactNode
}) {
    const { valor } = useEscopoContext()
    const total = arr(valor[id]).length

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className="h-10 min-w-[10rem] justify-between gap-2 font-normal shadow-xs hover:border-primary/40"
                >
                    <span className="truncate">{LABEL[id]}</span>
                    {total > 0 && (
                        <Badge
                            variant="secondary"
                            className="shrink-0 tabular-nums bg-primary/10 text-primary border border-primary/20 font-semibold"
                        >
                            {total}
                        </Badge>
                    )}
                    <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                arrow={false}
                onFocusOutside={e => e.preventDefault()}
                className="w-72 max-w-[calc(100vw-2rem)] overflow-hidden p-0 rounded-xl shadow-xl"
            >
                {children}
            </PopoverContent>
        </Popover>
    )
}
